import { useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getRevenueCombined } from '../../services'
import { ProductGroupRevenue } from '../../types'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { getColumns } from './columns'
import { formatCurrency, omit } from '@/utils/function'
import RevenueChart from './RevenueChart'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { listServiceCosts } from '../../../ServiceCosts/services'
import MonthlyStatistics from './MonthlyStatistics'
import Filters from '../shared/Filters'
import { useRevenueFilters } from '../../hooks/useRevenueFilters'
import Paper from '@/components/Paper'

const ProductGroups: React.FC = () => {
    const { t } = useTranslation()
    const { filters, setFilters } = useRevenueFilters()

    // Fetch all product groups from API
    const { data: productGroupOptions } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS],
        queryFn: getProductsGroupOptions,
    })

    // Get list of all product groups
    const allProductGroups = useMemo(() => {
        return productGroupOptions?.map((option) => option.value) || []
    }, [productGroupOptions])

    // State for selected product groups (shared between chart and table)
    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
        new Set()
    )

    // Initialize selectedProducts when allProductGroups changes
    useEffect(() => {
        if (allProductGroups.length > 0 && selectedProducts.size === 0) {
            setSelectedProducts(new Set(allProductGroups))
        }
    }, [allProductGroups, selectedProducts.size])

    // Fetch revenue data
    const { data: revenueResponse, isLoading: isLoadingRevenue } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.REPORTS_BI_REVENUE_ALLOCATIONS, filters],
            queryFn: () =>
                getRevenueCombined({
                    ...omit(filters, 'is_actual_revenue'),
                    ...(filters?.is_actual_revenue
                        ? { is_projected: false }
                        : {}),
                    group_by: 'product_group_name',
                }),
            staleTime: 5 * 60 * 1000,
        })

    const revenueData = revenueResponse?.results || []

    // Fetch cost data from service-costs API
    const { data: costData, isLoading: isLoadingCost } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_BI_SERVICE_COSTS, filters],
        queryFn: () =>
            listServiceCosts({
                from_month: filters?.gte,
                to_month: filters?.lte,
                product_group_names: filters?.product_group_names,
            }),
        staleTime: 5 * 60 * 1000,
    })

    // Process and aggregate data by product group
    const processedData = useMemo(() => {
        // Get all product group names from API
        const allProductGroups =
            productGroupOptions?.map((option) => option.value) || []

        if (allProductGroups.length === 0) return []

        // Group revenue by product_group_name
        const revenueByProduct = (revenueData || []).reduce(
            (acc, item) => {
                const productName = item.product_group_name
                if (!acc[productName]) {
                    acc[productName] = 0
                }
                acc[productName] +=
                    item.revenue_amount || item.allocated_amount || 0
                return acc
            },
            {} as Record<string, number>
        )

        // Separate specific and shared costs
        const specificCostByProduct: Record<string, number> = {}
        let totalSharedCost = 0

        ;(costData || []).forEach((item) => {
            if (item.cost_type === 'specific' && item.product_group_name) {
                const productName = item.product_group_name
                if (!specificCostByProduct[productName]) {
                    specificCostByProduct[productName] = 0
                }
                specificCostByProduct[productName] += item.cost_amount || 0
            } else if (item.cost_type === 'shared') {
                totalSharedCost += item.cost_amount || 0
            }
        })

        // Calculate total revenue from ALL products for shared cost allocation
        const totalRevenue = Object.values(revenueByProduct).reduce(
            (sum, rev) => sum + rev,
            0
        )

        // Build result array with ALL product groups from API
        const result: ProductGroupRevenue[] = allProductGroups
            .map((productName) => {
                const revenue = revenueByProduct[productName] || 0
                const specificCost = specificCostByProduct[productName] || 0

                // Allocate shared cost by revenue proportion
                const sharedCost =
                    totalRevenue > 0
                        ? (revenue / totalRevenue) * totalSharedCost
                        : 0

                const totalCost = specificCost + sharedCost
                const grossProfit = revenue - totalCost
                const percentage =
                    totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0

                return {
                    product_group_name: productName,
                    revenue,
                    cost: totalCost,
                    specific_cost: specificCost,
                    shared_cost: sharedCost,
                    gross_profit: grossProfit,
                    percentage,
                }
            })
            .sort((a, b) => b.revenue - a.revenue) // Sort by revenue descending

        // Calculate totals based on SELECTED products only
        const selectedTotalRevenue = result
            .filter((item) => selectedProducts.has(item.product_group_name))
            .reduce((sum, item) => sum + item.revenue, 0)
        const selectedTotalCost = result
            .filter((item) => selectedProducts.has(item.product_group_name))
            .reduce((sum, item) => sum + item.cost, 0)
        const selectedTotalSpecificCost = result
            .filter((item) => selectedProducts.has(item.product_group_name))
            .reduce((sum, item) => sum + item.specific_cost, 0)
        const selectedTotalSharedCost = result
            .filter((item) => selectedProducts.has(item.product_group_name))
            .reduce((sum, item) => sum + item.shared_cost, 0)
        const selectedTotalGrossProfit =
            selectedTotalRevenue - selectedTotalCost
        const selectedPercentage =
            totalRevenue > 0 ? (selectedTotalRevenue / totalRevenue) * 100 : 0

        // Add total row at the end (based on selected products)
        result.push({
            product_group_name: t('total'),
            revenue: selectedTotalRevenue,
            cost: selectedTotalCost,
            specific_cost: selectedTotalSpecificCost,
            shared_cost: selectedTotalSharedCost,
            gross_profit: selectedTotalGrossProfit,
            percentage: selectedPercentage,
            isTotal: true,
        })

        return result
    }, [revenueData, costData, productGroupOptions, selectedProducts, t])

    // Calculate totals based on SELECTED products only
    const totals = useMemo(() => {
        if (!processedData.length) {
            return {
                totalRevenue: 0,
                totalCost: 0,
                totalGrossProfit: 0,
            }
        }

        // Filter to only include selected products (exclude total row)
        const selectedData = processedData.filter(
            (item) =>
                !item.isTotal && selectedProducts.has(item.product_group_name)
        )

        return {
            totalRevenue: selectedData.reduce(
                (sum, item) => sum + item.revenue,
                0
            ),
            totalCost: selectedData.reduce((sum, item) => sum + item.cost, 0),
            totalGrossProfit: selectedData.reduce(
                (sum, item) => sum + item.gross_profit,
                0
            ),
        }
    }, [processedData, selectedProducts])

    const isLoading = isLoadingRevenue || isLoadingCost

    if (isLoading) {
        return <div className="p-4 text-center">{t('loading')}...</div>
    }

    return (
        <div className="space-y-2">
            <Filters onFiltersChange={setFilters} initialFilters={filters} />
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                {/* Total Revenue */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 col-span-2">
                    <div className="text-sm text-gray-600 mb-1">
                        {t('total_revenue')}
                    </div>
                    <div className="text-2xl font-bold text-blue-700">
                        {formatCurrency(totals.totalRevenue)}
                    </div>
                </div>

                {/* Total Cost */}
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                    <div className="text-sm text-gray-600 mb-1">
                        {t('total_cost')}
                    </div>
                    <div className="text-2xl font-bold text-orange-700">
                        {formatCurrency(totals.totalCost)}
                    </div>
                </div>

                {/* Total Gross Profit */}
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="text-sm text-gray-600 mb-1">
                        {t('total_gross_profit')}
                    </div>
                    <div className="text-2xl font-bold text-green-700">
                        {formatCurrency(totals.totalGrossProfit)}
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <RevenueChart
                    revenueData={revenueData || []}
                    costData={costData || []}
                    filters={filters}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                />
                <Paper>
                    <DataTableCustomized
                        columns={getColumns(t)}
                        rows={processedData}
                        rowKey={(row) => row.product_group_name}
                        rowClassName={(row) => {
                            if (row.isTotal) {
                                return 'bg-blue-50 font-bold border-t-2 border-blue-200'
                            }
                            if (!selectedProducts.has(row.product_group_name)) {
                                return 'opacity-30'
                            }
                            return ''
                        }}
                        size="small"
                    />
                </Paper>
            </div>
            {/* Monthly Statistics by Quarter */}
            <MonthlyStatistics
                revenueData={revenueData || []}
                selectedProducts={selectedProducts}
                filters={filters}
            />
        </div>
    )
}

export default ProductGroups
